import { ApiError, boardPath, type CardAddress } from '@molt/shared';

/**
 * Address resolution for boards and cards — chunk `D3`, agent G (`FE-08.4`).
 *
 * Three pure functions, deliberately in their own module rather than in
 * `lib/board-face.ts`: two routes call them (`/b/…` and `/c/…`) and a pure
 * module is the only kind either route can test without a router.
 *
 * ⚠ **Compare against the server's `path`, never a client-rebuilt one.**
 * `boardAddressSchema.path` and `cardAddressSchema.path` are assembled by
 * `packages/shared/src/address.ts`; rebuilding `/b/${shortLink}/${slug}` here
 * would be a second spelling of the same string, and the two will disagree on
 * the first board title whose slug the two rules treat differently.
 */

/**
 * Strip exactly one trailing `/`, and only when there is more than the root
 * to strip — `/` stays `/`. The server-assembled `canonicalPath` never ends in
 * `/` (`boardPath`/`cardPath` in `packages/shared/src/address.ts` build it as a
 * template literal that always ends on the slug or the short link), so
 * normalising is only ever needed on the browser's side of the comparison.
 *
 * The real defence against the loop is that `isCanonicalBoardPath` below is a
 * plain, pathname-only strict equality: whatever a caller navigates to on a
 * `false` result is `canonicalPath` itself, so the very next check is `true`
 * and the correction is always one hop. This strip is a defensive extra on
 * top of that, not the mechanism — `apps/web`'s router sets no
 * `trailingSlash` policy today, so a slash-bearing URL should not occur
 * through normal navigation, but the route's `{-$slug}` optional segment does
 * match a bare short link with a trailing slash (`/b/<shortLink>/`), and a
 * pasted or bookmarked URL is not the router's to normalise before this
 * function sees it.
 */
function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/**
 * True when the browser is already at the server's canonical spelling.
 *
 * ⚠ **Pathname only.** `FR-EXP-015` AC4 requires `/b/<shortLink>` with no slug
 * to resolve identically to the full form, and the search string must survive
 * the correction — comparing whole URLs would either loop or drop it. So this
 * takes bare pathnames, never a full URL or `location.href`, and the caller
 * (`FE-08.4`'s route) is the one that must keep any existing search string
 * when it navigates, not this function.
 *
 * `/b/<shortLink>` and `/b/<shortLink>/` (no slug, either way) are **not**
 * canonical — they still differ from `canonicalPath` as strings — so this
 * returns `false` for them and the caller corrects the address once, adding
 * the slug. `FR-EXP-015` AC4's "resolves identically" is about what the
 * visitor sees, not about the URL bar staying untouched.
 */
export function isCanonicalBoardPath(
  currentPathname: string,
  canonicalPath: string,
): boolean {
  return withoutTrailingSlash(currentPathname) === canonicalPath;
}

/**
 * True when the browser is already at the server's canonical card permalink.
 *
 * Same comparison as `isCanonicalBoardPath`, split out so E1 can consume the
 * card address the server already assembled instead of rebuilding it locally.
 */
export function isCanonicalCardPath(
  currentPathname: string,
  canonicalPath: string,
): boolean {
  return withoutTrailingSlash(currentPathname) === canonicalPath;
}

/**
 * Where a `/c/<shortLink>` visitor lands in `D3`: the card's **board**.
 *
 * ⚠ `FE-14.1` (chunk `E1`) opens the card *over* this board — `FR-EXP-015` AC2.
 * D3 resolves the address and stops, and the route's docstring says so by name.
 */
export function cardBoardPath(address: CardAddress): string {
  return boardPath(address.board.shortLink, address.board.slug);
}

export type BoardFallback = 'sign-in' | 'not-found' | 'error';

/**
 * `D3-BRIEF.md` §5.3, as one pure function.
 *
 * ⚠ **"No such board" and "a board you may not see" are the same 404**, on
 * purpose, so the route is not an existence oracle. The only distinction this
 * may draw is about the **viewer's session**, which is local knowledge:
 *
 *  - `not_found` **while signed out** → `'sign-in'` (`FR-PERM-001` AC1)
 *  - `not_found` with a session → `'not-found'`
 *  - anything else → `'error'`
 *
 * ⚠ `isSignedOut` is `isUnauthenticated(sessionError)`, **never `!profile`** —
 * an expired session keeps its last-good profile beside the error.
 */
export function boardFallbackFor(input: {
  error: unknown;
  isSignedOut: boolean;
}): BoardFallback {
  if (input.error instanceof ApiError && input.error.code === 'not_found') {
    return input.isSignedOut ? 'sign-in' : 'not-found';
  }
  return 'error';
}
